import { MainCard } from '@easydoctor/ui';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId, type ReactNode } from 'react';

/**
 * Blocco del dettaglio con titolo (h3) e contenuto, etichettato per le tecnologie assistive: una
 * `MainCard` di `@easydoctor/ui` sul fondo `grey.50` del pannello (#106, come le card del prototipo).
 * È la cornice comune dei blocchi: #3, #4 e #5 aggiungono i propri dentro la stessa.
 */
export function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <MainCard component="section" aria-labelledby={id}>
      <Stack spacing={3}>
        <Typography id={id} variant="subtitle2" component="h3">
          {title}
        </Typography>
        {children}
      </Stack>
    </MainCard>
  );
}

export interface Fact {
  label: string;
  /** `null` = dato assente: si mostra `missing`, mai un valore inventato. */
  value: ReactNode | null;
  /** Testo del dato assente (default «Non indicato»). */
  missing?: string;
}

/** Coppie etichetta-valore come lista di definizioni; i dati assenti sono dichiarati. */
export function FactList({ facts }: { facts: readonly Fact[] }) {
  return (
    <Box
      component="dl"
      sx={{ display: 'grid', gridTemplateColumns: { xs: '120px 1fr', sm: '160px 1fr' }, columnGap: 4, rowGap: 2, m: 0 }}
    >
      {facts.map((fact) => (
        <Box key={fact.label} sx={{ display: 'contents' }}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {fact.label}
          </Typography>
          <Typography
            component="dd"
            variant="body2"
            sx={{
              m: 0,
              minWidth: 0,
              overflowWrap: 'anywhere',
              color: fact.value === null ? 'text.disabled' : undefined,
            }}
          >
            {fact.value ?? fact.missing ?? 'Non indicato'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
